import {
    DASHBOARD_GRID_COLUMNS,
    DASHBOARD_GRID_MAX_H,
    DASHBOARD_GRID_MAX_Y,
    DASHBOARD_WIDGET_IDS,
    DASHBOARD_WIDGET_MIN_SIZE,
    DashboardLayout,
    DashboardWidgetLayout
} from '../models/dashboard-layout.model';
import { DASHBOARD_LAYOUT_VERSION, defaultDashboardLayout } from '../store/dashboard-layout-initial-state';

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(value, max));

const toInt = (value: unknown, fallback: number) =>
    typeof value === 'number' && Number.isFinite(value) ? Math.round(value) : fallback;

const sanitizeWidget = (widget: Partial<DashboardWidgetLayout>, fallback: DashboardWidgetLayout): DashboardWidgetLayout => {
    const { minW, minH } = DASHBOARD_WIDGET_MIN_SIZE[fallback.id];
    const w = clamp(toInt(widget.w, fallback.w), minW, DASHBOARD_GRID_COLUMNS);
    const h = clamp(toInt(widget.h, fallback.h), minH, DASHBOARD_GRID_MAX_H);

    return {
        id: fallback.id,
        visible: typeof widget.visible === 'boolean' ? widget.visible : fallback.visible,
        x: clamp(toInt(widget.x, fallback.x), 0, DASHBOARD_GRID_COLUMNS - w),
        y: clamp(toInt(widget.y, fallback.y), 0, DASHBOARD_GRID_MAX_Y),
        w,
        h
    };
};

const overlaps = (a: DashboardWidgetLayout, b: DashboardWidgetLayout) =>
    a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;

export const getLayoutBottom = (widgets: DashboardWidgetLayout[]) =>
    widgets.filter(widget => widget.visible).reduce((bottom, widget) => Math.max(bottom, widget.y + widget.h), 0);

export const sortByPosition = (widgets: DashboardWidgetLayout[]) =>
    [...widgets].sort((a, b) => a.y - b.y || a.x - b.x);

const overlapArea = (a: DashboardWidgetLayout, b: DashboardWidgetLayout) =>
    Math.max(0, Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x)) *
    Math.max(0, Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y));

// Widgets in `priority` are placed first, in the given order; the remaining visible widgets follow
// top-to-bottom. Any widget that collides with an already placed one is pushed down below the
// collider until it fits. Hidden widgets don't take part in the grid.
const placeWithPriority = (widgets: DashboardWidgetLayout[], priority: DashboardWidgetLayout[] = []): DashboardWidgetLayout[] => {
    const priorityIds = new Set(priority.map(widget => widget.id));
    const order = [...priority, ...sortByPosition(widgets.filter(widget => widget.visible && !priorityIds.has(widget.id)))];
    const placed: DashboardWidgetLayout[] = [];
    const resolved = new Map<string, DashboardWidgetLayout>();

    order.forEach(widget => {
        const candidate = { ...widget };
        let collider = placed.find(other => overlaps(candidate, other));
        while (collider) {
            candidate.y = collider.y + collider.h;
            collider = placed.find(other => overlaps(candidate, other));
        }
        placed.push(candidate);
        resolved.set(candidate.id, candidate);
    });

    return widgets.map(widget => resolved.get(widget.id) ?? widget);
};

const resolveOverlaps = (widgets: DashboardWidgetLayout[]) => placeWithPriority(widgets);

interface GridPoint {
    x: number;
    y: number;
}

// The swap target is the visible widget the dropped rectangle overlaps the most.
export const findSwapTarget = (widgets: DashboardWidgetLayout[], draggedId: string, drop: GridPoint): DashboardWidgetLayout | null => {
    const dragged = widgets.find(widget => widget.id === draggedId);
    if (!dragged) return null;

    const moved = { ...dragged, x: drop.x, y: drop.y };
    let target: DashboardWidgetLayout | null = null;
    let targetArea = 0;
    widgets.forEach(widget => {
        if (widget.id === draggedId || !widget.visible) return;
        const area = overlapArea(moved, widget);
        if (area > targetArea) {
            target = widget;
            targetArea = area;
        }
    });
    return target;
};

// Drop rules: the dragged widget lands at the drop position; if it overlaps another widget, the one
// with the largest overlap moves to the dragged widget's original x/y (a swap). Both are placed
// first, then everything else is pushed down only where it still collides.
export const resolveDrop = (widgets: DashboardWidgetLayout[], draggedId: string, drop: GridPoint): DashboardWidgetLayout[] => {
    const dragged = widgets.find(widget => widget.id === draggedId);
    if (!dragged) return widgets;

    const priority: DashboardWidgetLayout[] = [{ ...dragged, x: drop.x, y: drop.y }];
    const target = findSwapTarget(widgets, draggedId, drop);
    if (target) {
        priority.push({ ...target, x: clamp(dragged.x, 0, DASHBOARD_GRID_COLUMNS - target.w), y: dragged.y });
    }

    const updated = widgets.map(widget => priority.find(item => item.id === widget.id) ?? widget);
    return placeWithPriority(updated, priority);
};

// The resized widget keeps its new size and position; widgets it now overlaps are pushed down.
export const resolveResize = (widgets: DashboardWidgetLayout[], resized: Pick<DashboardWidgetLayout, 'id' | 'x' | 'y' | 'w' | 'h'>): DashboardWidgetLayout[] => {
    const current = widgets.find(widget => widget.id === resized.id);
    if (!current) return widgets;

    const updatedWidget = { ...current, x: resized.x, y: resized.y, w: resized.w, h: resized.h };
    const updated = widgets.map(widget => (widget.id === resized.id ? updatedWidget : widget));
    return placeWithPriority(updated, [updatedWidget]);
};

// A custom layout is stored per user, so it may come from an older app version or be edited elsewhere:
// a different version or broken shape means "no custom layout" (null, i.e. use the default), unknown or
// duplicate widgets are dropped, values are clamped to the grid rules, widgets the user never had are
// appended at the bottom and overlaps are pushed down.
export const normalizeCustomLayout = (layout: Partial<DashboardLayout> | null | undefined): DashboardLayout | null => {
    if (!layout || layout.version !== DASHBOARD_LAYOUT_VERSION || !Array.isArray(layout.widgets)) {
        return null;
    }

    const defaultsById = new Map(defaultDashboardLayout.widgets.map(widget => [widget.id, widget]));
    const seen = new Set<string>();
    const widgets: DashboardWidgetLayout[] = [];

    layout.widgets.forEach(widget => {
        const fallback = widget && defaultsById.get(widget.id);
        if (!fallback || seen.has(widget.id)) return;
        seen.add(widget.id);
        widgets.push(sanitizeWidget(widget, fallback));
    });

    DASHBOARD_WIDGET_IDS
        .filter(id => !seen.has(id))
        .forEach(id => {
            const fallback = defaultsById.get(id)!;
            widgets.push({ ...fallback, x: 0, y: Math.min(getLayoutBottom(widgets), DASHBOARD_GRID_MAX_Y) });
        });

    return {
        version: DASHBOARD_LAYOUT_VERSION,
        widgets: resolveOverlaps(widgets)
    };
};

// Used for widgets the current user can't see (e.g. the AI report for plain users): the widget is
// left out of the rendered grid, and if nothing else shares its rows that band collapses so no empty
// gap remains. Rendered positions at or below the band must be shifted back down before they're stored.
export interface CollapsedBand {
    top: number;
    height: number;
}

export const getCollapsedBand = (widgets: DashboardWidgetLayout[], id: DashboardWidgetLayout['id']): CollapsedBand | null => {
    const removed = widgets.find(widget => widget.id === id);
    if (!removed || !removed.visible) return null;

    const bandTop = removed.y;
    const bandBottom = removed.y + removed.h;
    const bandShared = widgets.some(widget =>
        widget.id !== id && widget.visible && widget.y < bandBottom && widget.y + widget.h > bandTop);

    return bandShared ? null : { top: bandTop, height: removed.h };
};

export const withoutWidget = (widgets: DashboardWidgetLayout[], id: DashboardWidgetLayout['id']) => {
    const band = getCollapsedBand(widgets, id);
    const rest = widgets.filter(widget => widget.id !== id);
    if (!band) return rest;

    return rest.map(widget => (widget.y >= band.top + band.height ? { ...widget, y: widget.y - band.height } : widget));
};

export const expandCollapsedBand = <T extends { y: number }>(positions: T[], band: CollapsedBand | null): T[] =>
    band ? positions.map(position => (position.y >= band.top ? { ...position, y: position.y + band.height } : position)) : positions;
